import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './cookiePopup.scss';

/**
 * `visible` defaults to true - matching react-snap's crawl, which always sees
 * an empty localStorage and therefore bakes the banner into every prerendered
 * page. Previously this defaulted to false and returned null until a
 * useEffect flipped it, so the client's very first hydration render had no
 * cookie-consent-banner element at all while the prerendered HTML did - a
 * structural mismatch (a whole subtree present on the server, missing on the
 * client) that hydration cannot reconcile and is exactly what was throwing
 * "Minified React error #418" on every single page load in production.
 * Rendering the same markup on both sides and hiding it with a CSS class
 * instead of unmounting it keeps the DOM shape identical, so hydration
 * always matches; only a returning visitor with consent already stored
 * (which prerendering can never know about) may see a brief flash before the
 * effect hides it, which is a cosmetic tradeoff, not a hydration error.
 *
 * `t` MUST come from useTranslation(), not a bare `import { t } from
 * 'i18next'`. That bare import used to be here, and it's what caused a real,
 * separate bug found while auditing every prerendered page's output: a bare
 * `t()` call reads whatever the i18next singleton's CURRENT resources
 * happen to be at the exact instant this component first renders - it isn't
 * a hook, so it never subscribes to "language finished loading" or
 * "language changed" and never re-renders after the fact. During
 * react-snap's crawl, later pages in the queue can start rendering before
 * their own language's translation file has fully replaced the previous
 * page's still-cached one (react-snap crawls with concurrency, and multiple
 * routes' loads overlap), so a page whose <html lang> and every OTHER piece
 * of text on it are correctly Armenian could still get a cookie banner
 * frozen in Spanish or English from whichever page loaded just before it -
 * confirmed by auditing the built output: build/hy/index.html,
 * build/hy/about/, build/hy/immigration/ and build/hy/personalinjury/ all
 * had this banner baked in with the WRONG language's text, and the same
 * pattern repeated at the /es/ -> /hy/ and /hy/ -> /ru/ crawl boundaries.
 * useTranslation() is what every other component in this app already uses;
 * it participates in react-i18next's Suspense-based readiness gating and
 * re-renders whenever the active language's resources actually change, so
 * this component can no longer get stuck showing a stale snapshot.
 */
const CookieConsentModal = ({ onConsentChange }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');

    if (consent) {
      // returning visitor who already made a choice - hide the banner and
      // restore their consent state
      setVisible(false);
      onConsentChange(consent === 'accepted');
    }
  }, [onConsentChange]);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    }

    onConsentChange(true);
    setVisible(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }

    onConsentChange(false);
    setVisible(false);
  };

  return (
    <div className={`cookie-consent-banner${visible ? '' : ' cookie-consent-banner--hidden'}`}>
      <p>{t('cookies.description')}</p>

      <div className="cookie-buttons">
        <button onClick={acceptCookies} className="accept">
          {t('cookies.accept')}
        </button>

        <button onClick={declineCookies} className="reject">
          {t('cookies.reject')}
        </button>
      </div>
    </div>
  );
};

export default CookieConsentModal;
